'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { toast } from 'sonner';
import type { Message, Attachment } from '../data/mockMessages';
import { useInboxTheme } from '@/contexts/InboxThemeContext';

interface MessageReaderProps {
  message: Message | null;
}

function AttachmentChip({ att, themeColor }: { att: Attachment; themeColor: string }) {
  const iconMap: Record<Attachment['type'], React.ReactNode> = {
    pdf: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF5F6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
    image: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4FFFB0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    zip: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFB830" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      </svg>
    ),
    doc: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7C5CFC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    other: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
        <polyline points="13 2 13 9 20 9" />
      </svg>
    ),
  };

  return (
    <button
      onClick={() => toast.success(`Downloading ${att.name} — connect Supabase Storage`)}
      className="flex items-center gap-2 px-3 py-2 rounded-lg inbox-glass border border-white/15 hover:bg-white/15 transition-all duration-150 group"
    >
      {iconMap[att.type]}
      <div className="text-left">
        <p className="text-xs font-medium inbox-text-foreground group-hover:inbox-text-primary transition-colors truncate max-w-[140px]">
          {att.name}
        </p>
        <p className="text-xs inbox-text-muted">{att.size}</p>
      </div>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto inbox-text-muted group-hover:inbox-text-primary transition-colors">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
  );
}

function formatFullDate(iso: string) {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function MessageReader({ message }: MessageReaderProps) {
  const [starred, setStarred] = useState(message?.starred ?? false);
  const { themeConfig } = useInboxTheme();

  if (!message) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center inbox-bg p-8 text-center">
        <div className="w-16 h-16 rounded-2xl inbox-glass border border-white/15 flex items-center justify-center mb-5">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="inbox-text-muted">
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
          </svg>
        </div>
        <h3 className="text-base font-semibold inbox-text-foreground mb-2">Select a message to read</h3>
        <p className="text-sm inbox-text-muted max-w-xs leading-relaxed">
          Choose a message from the list to view its full contents and any attachments.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in" style={{ background: 'rgba(0,0,0,0.15)' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 flex items-start justify-between gap-4 flex-shrink-0 inbox-glass-topbar">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-white/20">
            <AppImage
              src={message.avatarUrl}
              alt={message.avatarAlt}
              width={40}
              height={40}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold inbox-text-foreground leading-tight mb-1 truncate">
              {message.subject}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold inbox-text-foreground">{message.from}</span>
              <span className="text-xs inbox-text-muted font-mono">&lt;{message.fromEmail}&gt;</span>
            </div>
            <p className="text-xs inbox-text-muted mt-0.5">
              {formatFullDate(message.receivedAt)} · {message.size}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => { setStarred(!starred); toast.success(starred ? 'Removed from starred' : 'Added to starred'); }}
            className="p-2 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
            title={starred ? 'Remove from starred' : 'Star this message'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={starred ? '#FFB830' : 'none'} stroke={starred ? '#FFB830' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
          <button
            onClick={() => toast.info('Reply — connect Resend transactional email')}
            className="p-2 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
            title="Reply to this message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 17 4 12 9 7" />
              <path d="M20 18v-2a4 4 0 00-4-4H4" />
            </svg>
          </button>
          <button
            onClick={() => toast.info('Forward — connect Resend transactional email')}
            className="p-2 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
            title="Forward this message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 17 20 12 15 7" />
              <path d="M4 18v-2a4 4 0 014-4h12" />
            </svg>
          </button>
          <button
            onClick={() => toast.success('Message moved to trash')}
            className="p-2 rounded-lg inbox-text-muted hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
            title="Delete this message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
        <div
          className="text-sm leading-relaxed max-w-2xl inbox-text-secondary"
          dangerouslySetInnerHTML={{ __html: message.body }}
        />

        {/* Attachments */}
        {message.attachments.length > 0 && (
          <div className="mt-8 pt-5 border-t border-white/10">
            <p className="text-xs font-semibold inbox-text-muted uppercase tracking-wider mb-3">
              Attachments ({message.attachments.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {message.attachments.map((att) => (
                <AttachmentChip key={`att-${att.id}`} att={att} themeColor={themeConfig.color} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick reply bar */}
      <div className="px-6 py-4 border-t border-white/10 flex-shrink-0 inbox-glass">
        <button
          onClick={() => toast.info('Quick reply — connect Resend for transactional delivery')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl inbox-glass border border-white/15 text-sm inbox-text-muted hover:inbox-text-foreground hover:border-white/25 transition-all duration-150 text-left"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          Quick reply to {message.from}...
        </button>
      </div>
    </div>
  );
}